import { useCallback, useEffect, useState } from "react";
import { Bell, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnseen(data.unseen);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = async (open) => {
    if (open && unseen > 0) {
      try { await api.post("/notifications/seen"); } catch (e) { /* ignore */ }
      setUnseen(0);
      setItems((prev) => prev.map((n) => ({ ...n, seen: true })));
    }
  };

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card transition-colors hover:border-amber-400">
          <Bell className="h-4 w-4 text-amber-400" />
          {unseen > 0 && (
            <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1 font-mono text-[10px] font-bold text-[#0D1117]">
              {unseen > 9 ? "9+" : unseen}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="border-b border-border px-4 py-3">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Zmiany statusów</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p data-testid="notification-empty" className="px-4 py-8 text-center text-sm text-muted-foreground">Brak powiadomień.</p>
          ) : (
            items.map((n) => (
              <div key={n.id} data-testid={`notification-item-${n.order_number}`} className={`border-b border-border px-4 py-3 last:border-b-0 ${n.seen ? "" : "bg-amber-400/5"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-amber-300">{n.order_number}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{n.created_at.slice(0, 10)}</span>
                </div>
                {n.customer_name && <p className="mt-0.5 text-xs text-muted-foreground truncate">{n.customer_name}</p>}
                <div className="mt-2 flex items-center gap-2">
                  <StatusBadge status={n.old_status} />
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <StatusBadge status={n.new_status} />
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
